import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import useIsFieldRequired from '../../../utility/useIsFieldRequired';
import DATE_FORMATS from '../../../constants/dateFormats';
import { ISO_UTC_DATE_TIME_REGEX, DATE_ONLY_REGEX } from '../../../constants/regex';

const RMSDatePickerV2 = ({
  name,
  label,
  disabled = false,
  format = DATE_FORMATS.MM_DD_YYYY,
  minDate,
  maxDate,
  size = 'small',
  ...rest
}) => {
  const { control, trigger } = useFormContext();

  const isRequired = useIsFieldRequired(name);

  const parseDateOnly = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    if (typeof value === 'string') {
      // Case 1: plain date "YYYY-MM-DD"
      if (DATE_ONLY_REGEX.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day); // local midnight
      }

      // Case 2: full ISO datetime with Z (or offset)
      const isoMatch = value.match(ISO_UTC_DATE_TIME_REGEX);
      if (isoMatch) {
        // Use the UTC calendar date embedded in the timestamp,
        // NOT the browser's local conversion of that instant.
        const utcDate = new Date(value);
        return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
      }
    }

    return null;
  };

  const formatDateOnly = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <KeyboardDatePicker
            {...rest}
            autoOk
            fullWidth
            autoComplete="off"
            variant="inline"
            inputVariant="outlined"
            size={size}
            format={format}
            label={label}
            disabled={disabled || field.disabled}
            value={parseDateOnly(field.value)}
            minDate={minDate}
            maxDate={maxDate}
            onChange={(date) => {
              field.onChange(date ? formatDateOnly(date) : null);
              field.onChange(date);
              trigger(name);
            }}
            error={!!error}
            helperText={error?.message}
            onBlur={field.onBlur}
            required={isRequired}
          />
        )}
      />
    </MuiPickersUtilsProvider>
  );
};

export default RMSDatePickerV2;
